import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
  OAuthApi,
} from '@backstage/core-plugin-api';

export interface YamlFileResponse {
  content: string;
  sha: string;
  path: string;
  canEdit: boolean;
  repositoryUrl: string;
  defaultBranch: string;
}

export interface PermissionsResponse {
  canEdit: boolean;
  permission: 'admin' | 'write' | 'read' | 'none';
  repositoryUrl: string;
}

export interface ValidationError {
  /** Unique ID for this error */
  id: string;
  /** Line number (1-based) */
  line?: number;
  /** Column number (1-based) */
  column?: number;
  /** End line for range highlighting */
  endLine?: number;
  /** End column for range highlighting */
  endColumn?: number;
  /** Short error message */
  message: string;
  /** Detailed explanation of what's wrong */
  details?: string;
  /** Field path in the YAML (e.g., "metadata.name") */
  path?: string;
  /** Current invalid value */
  currentValue?: string;
  /** List of valid/suggested values */
  suggestions?: string[];
  /** Error severity */
  severity?: 'error' | 'warning' | 'info';
  /** Link to documentation */
  docsUrl?: string;
  /** Error code for categorization */
  code?: string;
}

export interface ValidateResponse {
  valid: boolean;
  errors: ValidationError[];
}

export interface SubmitPrResponse {
  prUrl: string;
  prNumber: number;
  branchName: string;
}

// Schema Info Types
export interface FieldInfo {
  name: string;
  path: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object' | 'entity-ref';
  required: boolean;
  description: string;
  examples?: string[];
  validValues?: string[];
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  docsUrl?: string;
}

export interface KindSchema {
  kind: string;
  description: string;
  docsUrl: string;
  apiVersion: string;
  fields: {
    root: FieldInfo[];
    metadata: FieldInfo[];
    spec: FieldInfo[];
  };
}

export interface AnnotationInfo {
  description: string;
  examples: string[];
  required?: boolean;
}

// ============================================================================
// AI Analysis Types
// ============================================================================

export type AiSuggestionType = 'add' | 'modify' | 'remove' | 'typo';
export type AiSuggestionSeverity = 'error' | 'warning' | 'info';

export interface AiSuggestion {
  /** Unique identifier for this suggestion */
  id: string;
  /** Type of suggestion */
  type: AiSuggestionType;
  /** Severity/importance level (error > warning > info) */
  severity: AiSuggestionSeverity;
  /** Priority for sorting (1 = highest, 4 = lowest) */
  priority: number;
  /** Short title describing the suggestion */
  title: string;
  /** Detailed description of what should be changed and why */
  description: string;
  /** YAML path affected (e.g., "metadata.annotations") */
  path: string;
  /** Line number in the YAML (1-based) */
  line: number;
  /** End line for multi-line suggestions */
  endLine?: number;
  /** Current value at this path (if exists) */
  currentValue?: string;
  /** Suggested new value */
  suggestedValue?: string;
  /** Diff information for display */
  diff?: {
    added: string;
    removed: string;
  };
}

export interface AnalyzeAiResponse {
  /** List of AI-generated suggestions */
  suggestions: AiSuggestion[];
  /** Summary of the analysis */
  summary: string;
  /** Processing time in milliseconds */
  processingTime: number;
}

export interface YamlEditorApi {
  getFile(entityRef: string, filePath?: string): Promise<YamlFileResponse>;
  getPermissions(entityRef: string): Promise<PermissionsResponse>;
  /**
   * Validate YAML content using local Zod schemas.
   * Fast validation suitable for real-time feedback while typing.
   */
  validateYaml(content: string): Promise<ValidateResponse>;
  /**
   * Validate YAML content using both local schemas AND the Catalog API.
   * Stricter validation for pre-submit checks. May include additional
   * validations from registered catalog processors.
   */
  validateYamlWithCatalog(content: string, entityRef?: string): Promise<ValidateResponse>;
  submitPullRequest(params: {
    entityRef: string;
    content: string;
    title: string;
    description: string;
    branchName?: string;
    filePath?: string;
  }): Promise<SubmitPrResponse>;
  
  // Schema Information
  /** Get all supported entity kinds */
  getKinds(): Promise<string[]>;
  /** Get schema for a specific kind */
  getKindSchema(kind: string): Promise<KindSchema | null>;
  /** Get all schemas and annotations */
  getAllSchemas(): Promise<{ schemas: Record<string, KindSchema>; annotations: Record<string, AnnotationInfo> }>;
  /** Get field info for a specific path */
  getFieldInfo(kind: string, path: string): Promise<FieldInfo | null>;
  /** Get required fields for a kind */
  getRequiredFields(kind: string): Promise<FieldInfo[]>;
  /** Get optional fields for a kind */
  getOptionalFields(kind: string): Promise<FieldInfo[]>;

  // AI Analysis
  /**
   * Get available AI models for analysis.
   */
  getAiModels(): Promise<{ models: string[] }>;
  
  /**
   * Analyze YAML content with AI and get improvement suggestions.
   * Uses MGA (myGenAssist) with Backstage context for intelligent analysis.
   * @param content - The YAML content to analyze
   * @param entityRef - Optional entity reference for context
   * @param validationErrors - Optional validation errors to include in context
   * @param model - Optional AI model to use (defaults to gpt-4o-mini)
   */
  analyzeWithAi(
    content: string,
    entityRef?: string,
    validationErrors?: Array<{ message: string; path?: string; line?: number }>,
    model?: string,
  ): Promise<AnalyzeAiResponse>;
}

export const yamlEditorApiRef = createApiRef<YamlEditorApi>({
  id: 'plugin.yaml-editor.api',
});

export class YamlEditorApiClient implements YamlEditorApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly githubAuthApi: OAuthApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    githubAuthApi: OAuthApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.githubAuthApi = options.githubAuthApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('yaml-editor');
  }

  private async getGitHubToken(): Promise<string | undefined> {
    try {
      // Request token with repo scope for read/write access
      const token = await this.githubAuthApi.getAccessToken(['repo']);
      return token;
    } catch {
      return undefined;
    }
  }

  async getFile(entityRef: string, filePath?: string): Promise<YamlFileResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams({ entityRef });
    if (filePath) {
      params.set('filePath', filePath);
    }

    const response = await this.fetchApi.fetch(
      `${baseUrl}/file?${params.toString()}`,
    );

    if (!response.ok) {
      let errorMessage = `Failed to fetch file: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        // Couldn't parse JSON
      }
      
      // Add helpful context for common errors
      if (response.status === 404) {
        errorMessage = 'Entity not found or does not have a valid GitHub source location.';
      } else if (response.status === 401) {
        errorMessage = 'Not authenticated. Please sign in to Backstage.';
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    
    // Try to get GitHub token for permission check
    const token = await this.getGitHubToken();
    if (token) {
      // Re-fetch with user's GitHub token for accurate permissions
      const tokenResponse = await this.fetchApi.fetch(
        `${baseUrl}/file?${params.toString()}`,
        {
          headers: {
            'X-GitHub-Token': token,
          },
        },
      );
      if (tokenResponse.ok) {
        return tokenResponse.json();
      }
    }

    return data;
  }

  async getPermissions(entityRef: string): Promise<PermissionsResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams({ entityRef });

    // First get basic response to find repository URL
    const basicResponse = await this.fetchApi.fetch(
      `${baseUrl}/permissions?${params.toString()}`,
    );

    if (!basicResponse.ok) {
      const error = await basicResponse.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Failed to check permissions: ${basicResponse.statusText}`);
    }

    const basicData = await basicResponse.json();
    
    // Try to get GitHub token for accurate permission check
    const token = await this.getGitHubToken();
    if (token) {
      // Use fetchApi for Backstage auth, X-GitHub-Token for GitHub token
      const tokenResponse = await this.fetchApi.fetch(
        `${baseUrl}/permissions?${params.toString()}`,
        {
          headers: {
            'X-GitHub-Token': token,
          },
        },
      );
      if (tokenResponse.ok) {
        return tokenResponse.json();
      }
    }

    return basicData;
  }

  async validateYaml(content: string): Promise<ValidateResponse> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(`${baseUrl}/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ content }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Failed to validate: ${response.statusText}`);
    }

    return response.json();
  }

  async validateYamlWithCatalog(content: string, entityRef?: string): Promise<ValidateResponse> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(`${baseUrl}/validate-catalog`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ content, entityRef }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Failed to validate with catalog: ${response.statusText}`);
    }

    return response.json();
  }

  async submitPullRequest(params: {
    entityRef: string;
    content: string;
    title: string;
    description: string;
    branchName?: string;
    filePath?: string;
  }): Promise<SubmitPrResponse> {
    const baseUrl = await this.getBaseUrl();
    
    // Get GitHub token with write scope
    const token = await this.getGitHubToken();
    if (!token) {
      throw new Error(
        'GitHub authentication required to create pull request. ' +
        'Please click "Authenticate" and grant repository access.'
      );
    }

    // Use fetchApi for Backstage auth, X-GitHub-Token for GitHub token
    // Note: Do NOT set Authorization header - fetchApi handles Backstage auth
    const response = await this.fetchApi.fetch(`${baseUrl}/submit-pr`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-GitHub-Token': token,
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      let errorMessage = `Failed to create PR: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
        
        // Provide helpful messages for common errors
        if (response.status === 401) {
          errorMessage = 'Authentication failed. Please try clicking "Authenticate" again to refresh your GitHub token.';
        } else if (response.status === 403) {
          errorMessage = 'You do not have write access to this repository. Please check your GitHub permissions.';
        }
      } catch {
        // Couldn't parse JSON error, use default message
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  // Schema Information Methods

  async getKinds(): Promise<string[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/schema/kinds`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch kinds');
    }
    
    const data = await response.json();
    return data.kinds;
  }

  async getKindSchema(kind: string): Promise<KindSchema | null> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/schema/kind/${encodeURIComponent(kind)}`);
    
    if (response.status === 404) {
      return null;
    }
    
    if (!response.ok) {
      throw new Error(`Failed to fetch schema for ${kind}`);
    }
    
    return response.json();
  }

  async getAllSchemas(): Promise<{ schemas: Record<string, KindSchema>; annotations: Record<string, AnnotationInfo> }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/schema/all`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch schemas');
    }
    
    return response.json();
  }

  async getFieldInfo(kind: string, path: string): Promise<FieldInfo | null> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams({ kind, path });
    const response = await this.fetchApi.fetch(`${baseUrl}/schema/field?${params}`);
    
    if (response.status === 404) {
      return null;
    }
    
    if (!response.ok) {
      throw new Error(`Failed to fetch field info for ${kind}.${path}`);
    }
    
    return response.json();
  }

  async getRequiredFields(kind: string): Promise<FieldInfo[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/schema/required/${encodeURIComponent(kind)}`);
    
    if (response.status === 404) {
      return [];
    }
    
    if (!response.ok) {
      throw new Error(`Failed to fetch required fields for ${kind}`);
    }
    
    const data = await response.json();
    return data.requiredFields;
  }

  async getOptionalFields(kind: string): Promise<FieldInfo[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/schema/optional/${encodeURIComponent(kind)}`);
    
    if (response.status === 404) {
      return [];
    }
    
    if (!response.ok) {
      throw new Error(`Failed to fetch optional fields for ${kind}`);
    }
    
    const data = await response.json();
    return data.optionalFields;
  }

  // AI Analysis Methods

  async getAiModels(): Promise<{ models: string[] }> {
    const baseUrl = await this.getBaseUrl();
    
    const response = await this.fetchApi.fetch(`${baseUrl}/ai/models`);
    
    if (!response.ok) {
      // Return default models on error
      return { models: ['gpt-4o-mini', 'gpt-4o'] };
    }
    
    return response.json();
  }

  async analyzeWithAi(
    content: string,
    entityRef?: string,
    validationErrors?: Array<{ message: string; path?: string; line?: number }>,
    model?: string,
  ): Promise<AnalyzeAiResponse> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(`${baseUrl}/analyze-ai`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ content, entityRef, validationErrors, model }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `AI analysis failed: ${response.statusText}`);
    }

    return response.json();
  }
}
